import { Component } from '@verndale/core';
import { defaultEasing } from '../animation/utils';
import { ElementOrSelector, stagger, timeline } from 'motion';
import { TimelineDefinition } from '@motionone/dom/types/timeline/types';
import { EventBusSingleton } from '../animation/eventBus';
import { disableBodyScroll } from 'body-scroll-lock';
import { breakpoints } from '../helpers/variables';

class IntroAnimationOverlay extends Component {
  MOTION_ZERO_DURATION = 0.000001;
  colorPalette: Array<string> | null = null;
  reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  breakpointTablet = window.matchMedia(`(min-width: ${breakpoints.tablet}px)`);
  hasIntroPlayed: string | null;

  constructor(el: HTMLElement) {
    super(el);
    this.colorPalette = [
      '#0f9d58',
      '#008768',
      '#492d8c',
      '#da291c',
      '#f47920',
      '#f4b450',
      '#2e67b2',
      '#009cde',
      '#492d8c',
      '#da291c',
      '#f47920',
      '#f4b450',
      '#2e67b2',
      '#009cde'
    ];
    this.hasIntroPlayed = localStorage.getItem('manhattan_intro-animation');

    if (!this.breakpointTablet.matches) {
      this.el.style.display = 'none';
      if (!this.reducedMotion.matches)
        EventBusSingleton.publish('introAnimationOverlay:hide');
      localStorage.setItem('manhattan_intro-animation', 'true');
      return;
    }

    if (this.hasIntroPlayed === null) {
      disableBodyScroll(
        document.querySelector('.homepage-masthead') as HTMLElement
      );
      if (!this.reducedMotion.matches && this.breakpointTablet.matches) {
        this.init();
        localStorage.setItem('manhattan_intro-animation', 'true');
      } else if (this.reducedMotion.matches || !this.breakpointTablet.matches) {
        this.el.style.display = 'none';
        if (!this.reducedMotion.matches)
          EventBusSingleton.publish('introAnimationOverlay:hide');
        localStorage.setItem('manhattan_intro-animation', 'true');
      }
    } else {
      this.el.style.display = 'none';
      if (!this.reducedMotion.matches)
        EventBusSingleton.publish('introAnimationOverlay:hide');
    }
  }

  setupDefaults() {
    this.dom = {
      content: this.el.querySelector<HTMLElement>(
        '.intro-animation-overlay__content'
      ),
      svgLogo: this.el.querySelector<HTMLElement>(
        '.intro-animation-overlay__logo'
      ),
      logoBars: this.el.querySelectorAll('.intro-animation-overlay__logo .bar'),
      logoLetters: this.el.querySelectorAll<HTMLElement>(
        '.intro-animation-overlay__logo .letter'
      ),
      logoTriangle: this.el.querySelector<HTMLElement>(
        '.intro-animation-overlay__logo .triangle'
      )
    };
  }

  async init() {
    const barsSequence = this.barsSequenceIn();
    const textSequence = this.textSequenceIn();
    [].forEach.call(this.dom.logoLetters, (letter: HTMLElement) => {
      letter.style.transform = 'translateY(100%)';
    });

    // uncover logo bars
    timeline(barsSequence, { duration: 1 });

    // stagger in logo letters
    await timeline(textSequence, {
      duration: 1,
      delay: 1 - 0.1
    }).finished;

    // iterate through color pallete for triangle in logo
    if (this.colorPalette)
      (this.dom.logoTriangle as HTMLElement).style.fill = this.colorPalette[0];
    const colorSequence = this.colorSequence();
    await timeline(colorSequence).finished;

    // let other components know that intro animation is starting to hide
    EventBusSingleton.publish('introAnimationOverlay:hide');

    const textSequenceOut = this.textSequenceOut();
    const barsSequenceOut = this.barsSequenceOut();
    const clipPathSequence = this.clipPathSequence();
    timeline(textSequenceOut, { duration: 1 });
    timeline(barsSequenceOut, {
      duration: 1,
      delay: 0.5
    });
    await timeline(clipPathSequence, { duration: 0.75, delay: 0.75 }).finished;
    this.el.style.display = 'none';
  }

  textSequenceIn() {
    const sequence = [] as TimelineDefinition;
    if (!this.dom.logoLetters) return sequence;
    sequence.push([
      this.dom.logoLetters as ElementOrSelector,
      {
        y: ['110%', 0]
      },
      {
        delay: stagger(0.08),
        easing: 'ease-out'
      }
    ]);

    return sequence;
  }

  textSequenceOut() {
    const sequence = [] as TimelineDefinition;
    if (!this.dom.logoLetters) return sequence;
    const reversedLetters = Array.from(
      this.dom.logoLetters as NodeListOf<HTMLElement>
    ).reverse();

    sequence.push([
      reversedLetters as ElementOrSelector,
      {
        y: [0, '-110%']
      },
      {
        delay: stagger(0.05),
        easing: 'ease-out'
      }
    ]);

    return sequence.reverse();
  }

  barsSequenceIn() {
    const sequence = [] as TimelineDefinition;
    const delays = [0.2, 0.15, 0.09, 0.05, 0];
    const durations = [0.1, 0.1, 0.1, 0.1, 0.1];
    const translations = [
      'translate(9px, -9px)',
      'translate(20px, -20px)',
      'translate(30px, -40px)',
      'translate(20px, -27px)',
      'translate(36px, -45px)'
    ];
    [].forEach.call(this.dom.logoBars, (bar: HTMLElement, index: number) => {
      sequence.push([
        bar as ElementOrSelector,
        {
          transform: ['translate(0, 0)', translations[index]]
        },
        {
          duration: durations[index],
          easing: defaultEasing,
          at: delays[index]
        }
      ]);
    });

    return sequence;
  }

  barsSequenceOut() {
    const sequence = [] as TimelineDefinition;
    const delays = [0, 0.05, 0.09, 0.15, 0.2];
    const durations = [0.1, 0.1, 0.1, 0.1, 0.1];
    const translations = [
      'translate(-22px, 40px)',
      'translate(-22px, 40px)',
      'translate(-22px, 40px)',
      'translate(-22px, 40px)',
      'translate(-22px, 40px)'
    ];

    [].forEach.call(this.dom.logoBars, (bar: HTMLElement, index: number) => {
      sequence.push([
        bar as ElementOrSelector,
        {
          transform: [translations[index], 'translate(0, 0)']
        },
        {
          duration: durations[index],
          easing: defaultEasing,
          at: delays[index]
        }
      ]);
    });

    return sequence;
  }

  colorSequence() {
    const sequence = [] as TimelineDefinition;
    this.colorPalette?.forEach(color => {
      sequence.push([
        this.dom.logoTriangle as ElementOrSelector,
        {
          fill: color
        },
        {
          duration: this.MOTION_ZERO_DURATION,
          delay: 0.3
        }
      ]);
    });
    return sequence;
  }

  clipPathSequence() {
    // clip-path: polygon(0 0, 100% 0%, 100% 100%, 0% 100%);
    const sequence = [] as TimelineDefinition;

    sequence.push([
      this.el as ElementOrSelector,
      {
        clipPath: [
          'polygon(0 0, 150% 0%, 100% 100%, 0% 100%)',
          'polygon(0 0, 0% 0%, -50% 100%, 0% 100%)'
        ]
      }
    ]);

    return sequence;
  }
}

export default IntroAnimationOverlay;
